import React, { useState, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Box, IconButton, Typography, makeStyles, CircularProgress } from '@material-ui/core';
import { Divider } from '@material-ui/core';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import LastPageIcon from '@material-ui/icons/LastPage';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

// Type assertions to fix TypeScript compatibility issues with react-pdf
// These are needed due to React type definition conflicts between react-pdf and @types/react
const TypedDocument = Document as any;
const TypedPage = Page as any;

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxHeight: '100%',
        overflow: 'auto',
    },
    controls: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(1),
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        borderRadius: '8px',
        marginBottom: theme.spacing(2),
        position: 'sticky',
        top: 0,
        zIndex: 10,
        flexWrap: 'wrap',
    },
    controlButton: {
        color: '#fff',
        padding: theme.spacing(0.5),
        '&:disabled': {
            color: 'rgba(255, 255, 255, 0.3)',
        },
    },
    pageInfo: {
        color: '#fff',
        margin: `0 ${theme.spacing(1)}px`,
        minWidth: '100px',
        textAlign: 'center',
    },
    zoomInfo: {
        color: '#fff',
        margin: `0 ${theme.spacing(1)}px`,
        minWidth: '60px',
        textAlign: 'center',
    },
    documentContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
    },
    pageWrapper: {
        marginBottom: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(4),
    },
    errorContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(4),
        color: theme.palette.error.main,
    },
}));

export interface PDFViewerV2Props {
    /** PDF file URL, File object, or ArrayBuffer */
    file: string | File | ArrayBuffer | null | undefined;
    /** Initial scale/zoom level (default: 1.2) */
    initialScale?: number;
    /** Minimum scale (default: 0.5) */
    minScale?: number;
    /** Maximum scale (default: 3.0) */
    maxScale?: number;
    /** Scale increment/decrement step (default: 0.2) */
    scaleStep?: number;
    /** Show page navigation controls (default: true) */
    showControls?: boolean;
    /** Show zoom controls (default: true) */
    showZoomControls?: boolean;
    /** Show all pages at once or single page view (default: true for all pages) */
    showAllPages?: boolean;
    /** Custom loading message */
    loadingMessage?: string;
    /** Custom error message */
    errorMessage?: string;
    /** Callback when document loads successfully */
    onLoadSuccess?: (numPages: number) => void;
    /** Callback when document fails to load */
    onLoadError?: (error: Error) => void;
    /** Additional props to pass to Document component */
    documentProps?: Record<string, any>;
    /** Additional props to pass to Page component */
    pageProps?: Record<string, any>;
}

const PDFViewerV2: React.FC<PDFViewerV2Props> = ({
    file,
    initialScale = 1.2,
    minScale = 0.5,
    maxScale = 3.0,
    scaleStep = 0.2,
    showControls = true,
    showZoomControls = true,
    showAllPages = true,
    loadingMessage = 'Loading PDF...',
    errorMessage = 'Failed to load PDF',
    onLoadSuccess,
    onLoadError,
    documentProps = {},
    pageProps = {},
}) => {
    const classes = useStyles();
    const [numPages, setNumPages] = useState<number | null>(null);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [scale, setScale] = useState<number>(initialScale);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);

    const handleDocumentLoadSuccess = useCallback(
        ({ numPages }: { numPages: number }) => {
            setNumPages(numPages);
            setPageNumber(1);
            setLoading(false);
            setError(null);
            onLoadSuccess?.(numPages);
        },
        [onLoadSuccess]
    );

    const handleDocumentLoadError = useCallback(
        (error: Error) => {
            setLoading(false);
            setError(error);
            onLoadError?.(error);
        },
        [onLoadError]
    );

    const handlePreviousPage = useCallback(() => {
        setPageNumber((prev) => Math.max(1, prev - 1));
    }, []);

    const handleNextPage = useCallback(() => {
        setPageNumber((prev) => Math.min(numPages || 1, prev + 1));
    }, [numPages]);

    const handleFirstPage = useCallback(() => {
        setPageNumber(1);
    }, []);

    const handleLastPage = useCallback(() => {
        setPageNumber(numPages || 1);
    }, [numPages]);

    const handleZoomIn = useCallback(() => {
        setScale((prev) => Math.min(maxScale, prev + scaleStep));
    }, [maxScale, scaleStep]);

    const handleZoomOut = useCallback(() => {
        setScale((prev) => Math.max(minScale, prev - scaleStep));
    }, [minScale, scaleStep]);

    // Reserved for future use - zoom reset functionality
    // const handleZoomReset = useCallback(() => {
    //     setScale(initialScale);
    // }, [initialScale]);

    if (!file) {
        return (
            <Box className={classes.errorContainer}>
                <Typography variant="body1">No PDF file provided</Typography>
            </Box>
        );
    }

    return (
        <Box className={classes.container}>
            {showControls && (
                <Box className={classes.controls}>
                    {!showAllPages && numPages !== null && (
                        <>
                            <IconButton
                                className={classes.controlButton}
                                onClick={handleFirstPage}
                                disabled={pageNumber <= 1}
                                size="small"
                                title="First Page"
                            >
                                <FirstPageIcon />
                            </IconButton>
                            <IconButton
                                className={classes.controlButton}
                                onClick={handlePreviousPage}
                                disabled={pageNumber <= 1}
                                size="small"
                                title="Previous Page"
                            >
                                <NavigateBeforeIcon />
                            </IconButton>
                            <Typography className={classes.pageInfo} variant="body2">
                                Page {pageNumber} of {numPages}
                            </Typography>
                            <IconButton
                                className={classes.controlButton}
                                onClick={handleNextPage}
                                disabled={pageNumber >= numPages}
                                size="small"
                                title="Next Page"
                            >
                                <NavigateNextIcon />
                            </IconButton>
                            <IconButton
                                className={classes.controlButton}
                                onClick={handleLastPage}
                                disabled={pageNumber >= numPages}
                                size="small"
                                title="Last Page"
                            >
                                <LastPageIcon />
                            </IconButton>
                        </>
                    )}
                    {showZoomControls && (
                        <>
                            {!showAllPages && numPages !== null && (
                                <Divider
                                    orientation="vertical"
                                    flexItem
                                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.3)', margin: '0 8px' }}
                                />
                            )}
                            <IconButton
                                className={classes.controlButton}
                                onClick={handleZoomOut}
                                disabled={scale <= minScale}
                                size="small"
                                title="Zoom Out"
                            >
                                <ZoomOutIcon />
                            </IconButton>
                            <Typography className={classes.zoomInfo} variant="body2">
                                {Math.round(scale * 100)}%
                            </Typography>
                            <IconButton
                                className={classes.controlButton}
                                onClick={handleZoomIn}
                                disabled={scale >= maxScale}
                                size="small"
                                title="Zoom In"
                            >
                                <ZoomInIcon />
                            </IconButton>
                        </>
                    )}
                </Box>
            )}

            {loading && (
                <Box className={classes.loadingContainer}>
                    <CircularProgress />
                    <Typography variant="body2" style={{ marginTop: 16 }}>
                        {loadingMessage}
                    </Typography>
                </Box>
            )}

            {error && (
                <Box className={classes.errorContainer}>
                    <Typography variant="body1">{errorMessage}</Typography>
                    <Typography variant="body2" style={{ marginTop: 8 }}>
                        {error.message}
                    </Typography>
                </Box>
            )}

            <Box className={classes.documentContainer}>
                <TypedDocument
                    file={file}
                    onLoadSuccess={handleDocumentLoadSuccess}
                    onLoadError={handleDocumentLoadError}
                    loading={
                        <Box className={classes.loadingContainer}>
                            <CircularProgress />
                            <Typography variant="body2" style={{ marginTop: 16 }}>
                                {loadingMessage}
                            </Typography>
                        </Box>
                    }
                    error={
                        <Box className={classes.errorContainer}>
                            <Typography variant="body1">{errorMessage}</Typography>
                        </Box>
                    }
                    {...documentProps}
                >
                    {showAllPages && numPages !== null
                        ? Array.from(new Array(numPages), (_, index) => (
                              <Box key={`page-${index + 1}`} className={classes.pageWrapper}>
                                  <TypedPage
                                      pageNumber={index + 1}
                                      scale={scale}
                                      renderTextLayer={true}
                                      renderAnnotationLayer={true}
                                      {...pageProps}
                                  />
                                  {index < numPages - 1 && (
                                      <Divider style={{ width: '100%', marginTop: 16, marginBottom: 16 }} />
                                  )}
                              </Box>
                          ))
                        : numPages !== null && (
                              <Box className={classes.pageWrapper}>
                                  <TypedPage
                                      pageNumber={pageNumber}
                                      scale={scale}
                                      renderTextLayer={true}
                                      renderAnnotationLayer={true}
                                      {...pageProps}
                                  />
                              </Box>
                          )}
                </TypedDocument>
            </Box>
        </Box>
    );
};

export default PDFViewerV2;
